import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuth } from '../../utils/api';

// Use relative paths — fetchAuth in api.js handles the base URL correctly

function UsersView({ handleApiError, showNotification, user }) {
    const [users, setUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBillingUserId, setEditingBillingUserId] = useState(null);
    const [billingForm, setBillingForm] = useState({ billingName: '', billingAddress: '', billingContact: '' });

    const fetchUsers = useCallback(async () => {
        try {
            const res = await fetchAuth('/api/users');
            if (!res.ok) throw new Error('Failed to fetch users');
            setUsers(await res.json());
        } catch (error) { handleApiError(error); }
    }, [handleApiError]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleAddUser = async (newUser) => {
        if (!newUser.username || !newUser.password || !newUser.role) {
            showNotification('Username, Password, and Role are required.', 'error');
            return;
        }
        try {
            const res = await fetchAuth('/api/users', { method: 'POST', body: JSON.stringify(newUser) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            showNotification('User added successfully!', 'success');
            setIsModalOpen(false);
            fetchUsers();
        } catch (err) { handleApiError(err); }
    };

    const handleDeleteUser = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to delete the user: ${username}?`)) return;
        try {
            const res = await fetchAuth(`/api/users/${userId}`, { method: 'DELETE' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            showNotification('User deleted.', 'success');
            fetchUsers();
        } catch (err) { handleApiError(err); }
    };

    const startEditingBilling = (u) => {
        setEditingBillingUserId(u._id || u.id);
        setBillingForm({
            billingName: u.billingName || '',
            billingAddress: u.billingAddress || '',
            billingContact: u.billingContact || '',
        });
    };

    const saveBillingProfile = async (userId) => {
        try {
            const res = await fetchAuth('/api/billing-profile', {
                method: 'POST',
                body: JSON.stringify({ userId, ...billingForm })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            showNotification('Billing profile saved!', 'success');
            setEditingBillingUserId(null);
            fetchUsers();
        } catch (err) { handleApiError(err); }
    };

    const AddUserModal = ({ onClose, onSave }) => {
        const [newUser, setNewUser] = useState({ username: '', password: '', role: 'Member', phone: '' });
        const handleInputChange = e => setNewUser(prev => ({ ...prev, [e.target.name]: e.target.value }));
        const handleSubmit = e => { e.preventDefault(); onSave(newUser); };

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h3>Add New User</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="input-group">
                            <label>Username</label>
                            <input name="username" value={newUser.username} onChange={handleInputChange} required />
                        </div>
                        <div className="input-group">
                            <label>Phone Number (Optional)</label>
                            <input name="phone" type="text" value={newUser.phone} onChange={handleInputChange} />
                        </div>
                        <div className="input-group">
                            <label>Password</label>
                            <input type="password" name="password" value={newUser.password} onChange={handleInputChange} required />
                        </div>
                        <div className="input-group">
                            <label>Role</label>
                            <select name="role" value={newUser.role} onChange={handleInputChange}>
                                <option value="Member">Member</option>
                                <option value="Co-admin">Co-admin</option>
                                {user.role === 'Admin' && <option value="Admin">Admin</option>}
                            </select>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                            <button type="submit" className="confirm-btn">Add User</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className="page-container">
            {isModalOpen && <AddUserModal onClose={() => setIsModalOpen(false)} onSave={handleAddUser} />}
            <div className="page-header">
                <h2 className="page-title">User Management</h2>
                <button className="btn-primary add-lead-btn" onClick={() => setIsModalOpen(true)}>+ Add User</button>
            </div>
            <div className="data-display">
                <h3>All Users</h3>
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Role</th>
                                <th>Billing Name (on Bills)</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => {
                                const uid = u._id || u.id;
                                const isEditing = editingBillingUserId === uid;
                                return (
                                    <React.Fragment key={uid}>
                                        <tr>
                                            <td>{u.username}</td>
                                            <td>{u.role}</td>
                                            <td>
                                                <span style={{ color: u.billingName ? 'var(--color-text)' : 'var(--color-text-muted)', fontStyle: u.billingName ? 'normal' : 'italic' }}>
                                                    {u.billingName || 'Using company default'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="table-actions">
                                                    <button
                                                        className="icon-btn edit"
                                                        title="Edit billing profile"
                                                        onClick={() => isEditing ? setEditingBillingUserId(null) : startEditingBilling(u)}
                                                        style={{ fontSize: '12px', padding: '4px 8px', width: 'auto', borderRadius: '4px' }}
                                                    >
                                                        {isEditing ? 'Close' : '✏️ Billing'}
                                                    </button>
                                                    {user.role === 'Admin' && user.username !== u.username && (
                                                        <button className="icon-btn delete" onClick={() => handleDeleteUser(uid, u.username)}>
                                                            <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {isEditing && (
                                            <tr className="expanded-row">
                                                <td colSpan={4} style={{ backgroundColor: 'var(--color-bg-light)', padding: '16px' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
                                                        <div className="input-group" style={{ margin: 0 }}>
                                                            <label>Billing Name (shown on bills)</label>
                                                            <input
                                                                value={billingForm.billingName}
                                                                onChange={e => setBillingForm(p => ({ ...p, billingName: e.target.value }))}
                                                                placeholder="e.g. Rudra Traders"
                                                            />
                                                        </div>
                                                        <div className="input-group" style={{ margin: 0 }}>
                                                            <label>Billing Address</label>
                                                            <input
                                                                value={billingForm.billingAddress}
                                                                onChange={e => setBillingForm(p => ({ ...p, billingAddress: e.target.value }))}
                                                                placeholder="Custom address for bills"
                                                            />
                                                        </div>
                                                        <div className="input-group" style={{ margin: 0 }}>
                                                            <label>Billing Contact</label>
                                                            <input
                                                                value={billingForm.billingContact}
                                                                onChange={e => setBillingForm(p => ({ ...p, billingContact: e.target.value }))}
                                                                placeholder="Phone / Email for bills"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                                                        <button className="btn-primary" onClick={() => saveBillingProfile(uid)}>Save Billing Profile</button>
                                                        <button className="btn-secondary" onClick={() => setEditingBillingUserId(null)}>Cancel</button>
                                                    </div>
                                                    <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                                                        💡 Leave blank to use the company's default bill settings.
                                                    </p>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default UsersView;
